import { ONE_SIZE, sizeCounts } from "./sizes.ts";
import { GARMENTS_WINDOW } from "./turnaround.ts";
import type { Spot } from "./types.ts";

// Customer-supplied garments (spec 2026-10-07 section 2): the buyer sends their own shirts, we print and press. The
// page bundles this file too: it imports only types, the size codes and the turnaround words, so the designer and the server agree.

export const OWN_TYPES = ["tee", "longsleeve", "hoodie", "crewneck", "tank", "tote"] as const;
export type OwnType = (typeof OWN_TYPES)[number];

// "Customer's own tee", "Customer's long sleeve (Black)".
export const OWN_LABEL: Record<OwnType, string> = {
  tee: "tee", longsleeve: "long sleeve", hoodie: "hoodie", crewneck: "crewneck", tank: "tank", tote: "tote",
};
// The type buttons on the designer.
export const OWN_BUTTON: Record<OwnType, string> = {
  tee: "Tee", longsleeve: "Long sleeve", hoodie: "Hoodie", crewneck: "Crewneck", tank: "Tank", tote: "Tote",
};

const SHIRT_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL"];
export const OWN_SIZES: Record<OwnType, string[]> = {
  tee: SHIRT_SIZES, longsleeve: SHIRT_SIZES, hoodie: SHIRT_SIZES, crewneck: SHIRT_SIZES, tank: SHIRT_SIZES, tote: [ONE_SIZE],
};

// The preview's colors: common garment colors, only for the picture (the buyer's garments are whatever they send). The
// tone picks the mockup blend as a photo's tone does (src/image/tone.ts).
export interface OwnColor {
  key: string;
  name: string;
  hex: string;
  tone: "light" | "dark";
}
export const OWN_COLORS: readonly OwnColor[] = [
  { key: "white", name: "White", hex: "#ffffff", tone: "light" },
  { key: "natural", name: "Natural", hex: "#ece3cf", tone: "light" },
  { key: "heather-gray", name: "Heather gray", hex: "#b9b9b6", tone: "light" },
  { key: "gold", name: "Gold", hex: "#f0b92e", tone: "light" },
  { key: "orange", name: "Orange", hex: "#e2662a", tone: "dark" },
  { key: "red", name: "Red", hex: "#b8212d", tone: "dark" },
  { key: "maroon", name: "Maroon", hex: "#5e1b28", tone: "dark" },
  { key: "royal", name: "Royal blue", hex: "#2851a3", tone: "dark" },
  { key: "navy", name: "Navy", hex: "#1f2a44", tone: "dark" },
  { key: "forest", name: "Forest green", hex: "#244a32", tone: "dark" },
  { key: "charcoal", name: "Charcoal", hex: "#3f4043", tone: "dark" },
  { key: "black", name: "Black", hex: "#1b1b1c", tone: "dark" },
];

export function ownColor(key: unknown): OwnColor | undefined {
  return OWN_COLORS.find((c) => c.key === key);
}

export const isOwnType = (v: unknown): v is OwnType => typeof v === "string" && (OWN_TYPES as readonly string[]).includes(v);

// What an own-garment request carries (mockups.own, order_items.own).
export interface OwnGarment {
  garmentType: OwnType;
  color: string;
}

// The one "color" an own-garment request has: the page keys its quantities and estimate by it, the stored tiles too.
export const OWN_ID = "own";

// "Customer's own tee", for the request's lines, the Slack note and the email.
export const ownGarmentLine = (type: OwnType) => `Customer's own ${OWN_LABEL[type]}`;
// "Customer's own tee, Black: M 6, L 2", "Customer's own tote, Natural: One size 25".
export function ownLine(o: OwnGarment, quantities: Record<string, number>): string {
  return `${ownGarmentLine(o.garmentType)}, ${ownColor(o.color)?.name ?? o.color}: ${sizeCounts(quantities)}`;
}

// The order item's garment name on the card, the receipt and the press: "Customer's tee (Black)".
export const ownItemName = (o: OwnGarment) => `Customer's ${OWN_LABEL[o.garmentType]} (${ownColor(o.color)?.name ?? o.color})`;

// ---- the generic silhouettes: one nominal 1000 x 1250 canvas like an S&S photo, at OWN_PPI pixels an inch ----

export const OWN_CANVAS = { width: 1000, height: 1250 } as const;
export const OWN_PPI = 24;

export interface OwnSpot {
  x: number;
  y: number;
  w: number;
  h: number;
  maxWidthIn: number;
  maxHeightIn: number;
}

// A spot's box from its center line and top edge in pixels and its largest print in inches (the box is that print).
function spot(centerX: number, top: number, maxWidthIn: number, maxHeightIn: number): OwnSpot {
  const w = maxWidthIn * OWN_PPI;
  return { x: centerX - w / 2, y: top, w, h: maxHeightIn * OWN_PPI, maxWidthIn, maxHeightIn };
}

// Front center and full back: 12 x 16 in (12 x 12 under a hood's pocket and on a tote); left chest: 4 x 4 in on the
// wearer's left, the viewer's right. A tote prints on its front only. Full back is measured on the back silhouette.
export const OWN_SPOTS: Record<OwnType, Partial<Record<Spot, OwnSpot>>> = {
  tee: { "front-center": spot(500, 280, 12, 16), "left-chest": spot(596, 285, 4, 4), "full-back": spot(500, 250, 12, 16) },
  longsleeve: { "front-center": spot(500, 280, 12, 16), "left-chest": spot(596, 285, 4, 4), "full-back": spot(500, 250, 12, 16) },
  crewneck: { "front-center": spot(500, 290, 12, 16), "left-chest": spot(600, 290, 4, 4), "full-back": spot(500, 260, 12, 16) },
  hoodie: { "front-center": spot(500, 330, 12, 12), "left-chest": spot(600, 330, 4, 4), "full-back": spot(500, 320, 12, 16) },
  tank: { "front-center": spot(500, 360, 12, 16), "left-chest": spot(588, 380, 4, 4), "full-back": spot(500, 300, 12, 16) },
  tote: { "front-center": spot(500, 590, 12, 12) },
};

export type OwnSide = "front" | "back";
export const ownSides = (type: OwnType): OwnSide[] => (type === "tote" ? ["front"] : ["front", "back"]);

// Each shape is one filled outline plus faint detail strokes (collar, hems, cuffs, hood, pocket). Paths are symmetric
// about x = 500.
interface Shape {
  outline: string;
  // Filled darker (inside of a hood), drawn over the body.
  shade?: string;
  details: string[];
  // Strokes in the garment's own color (a tote's handles), drawn before the outline.
  straps?: string;
}

const TEE_BODY = "L705,205 L880,345 L800,430 L745,385 L750,930 Q500,948 250,930 L255,385 L200,430 L120,345 L295,205 Z";
const LONG_BODY = "L705,205 L790,270 L925,770 L855,795 L745,420 L750,930 Q500,948 250,930 L255,420 L145,795 L75,770 L210,270 L295,205 Z";
const CREW_BODY = "L715,210 L800,275 L935,780 L860,805 L760,430 L768,960 Q500,975 232,960 L240,430 L140,805 L65,780 L200,275 L285,210 Z";
const SHIRT_HEM = "M253,905 Q500,922 747,905";
const LONG_CUFFS = ["M917,742 L847,767", "M83,742 L153,767"];
const CREW_DETAILS = ["M236,905 Q500,920 764,905", "M927,750 L852,775", "M73,750 L148,775"];

const SHAPES: Record<OwnType, Partial<Record<OwnSide, Shape>>> = {
  tee: {
    front: { outline: `M425,175 Q500,245 575,175 ${TEE_BODY}`, details: ["M425,175 Q500,245 575,175", "M862,330 L784,414", "M138,330 L216,414", SHIRT_HEM] },
    back: { outline: `M425,175 Q500,200 575,175 ${TEE_BODY}`, details: ["M425,175 Q500,200 575,175", "M862,330 L784,414", "M138,330 L216,414", SHIRT_HEM] },
  },
  longsleeve: {
    front: { outline: `M425,175 Q500,245 575,175 ${LONG_BODY}`, details: ["M425,175 Q500,245 575,175", ...LONG_CUFFS, SHIRT_HEM] },
    back: { outline: `M425,175 Q500,200 575,175 ${LONG_BODY}`, details: ["M425,175 Q500,200 575,175", ...LONG_CUFFS, SHIRT_HEM] },
  },
  crewneck: {
    front: { outline: `M430,180 Q500,232 570,180 ${CREW_BODY}`, details: ["M430,180 Q500,232 570,180", ...CREW_DETAILS] },
    back: { outline: `M430,180 Q500,205 570,180 ${CREW_BODY}`, details: ["M430,180 Q500,205 570,180", ...CREW_DETAILS] },
  },
  hoodie: {
    front: {
      outline: `M285,210 C300,40 700,40 715,210 ${CREW_BODY.replace("L715,210 ", "")}`,
      shade: "M400,220 C395,105 605,105 600,220 Q500,275 400,220 Z",
      details: ["M455,255 L450,385", "M545,255 L550,385", "M370,650 L630,650 L680,810 L320,810 Z", ...CREW_DETAILS],
    },
    back: {
      outline: `M285,210 C300,40 700,40 715,210 ${CREW_BODY.replace("L715,210 ", "")}`,
      details: ["M500,62 L500,232", "M318,214 Q500,262 682,214", ...CREW_DETAILS],
    },
  },
  tank: {
    front: { outline: "M405,165 Q500,330 595,165 L655,165 Q665,330 745,385 L750,930 Q500,948 250,930 L255,385 Q335,330 345,165 Z", details: ["M405,165 Q500,330 595,165", SHIRT_HEM] },
    back: { outline: "M405,165 Q500,255 595,165 L655,165 Q665,330 745,385 L750,930 Q500,948 250,930 L255,385 Q335,330 345,165 Z", details: ["M405,165 Q500,255 595,165", SHIRT_HEM] },
  },
  tote: {
    front: { outline: "M290,470 L710,470 L722,1010 L278,1010 Z", straps: "M380,476 C380,250 620,250 620,476", details: ["M292,505 L708,505"] },
  },
};

// The silhouette as an SVG document: the shape filled with `fill` (a hex color; the page shows it as a data URL, the
// server renders it with sharp), with a faint outline so a white garment still reads on a white page. `background` paints the
// canvas (the server's render needs one; the page leaves it clear).
export function silhouetteSvg(type: OwnType, side: OwnSide, fill: string, background?: string): string {
  const s = SHAPES[type][side] ?? SHAPES[type].front!;
  const { width, height } = OWN_CANVAS;
  const faint = 'fill="none" stroke="#000" stroke-opacity="0.14" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"';
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
    ...(background ? [`<rect width="${width}" height="${height}" fill="${background}"/>`] : []),
    ...(s.straps ? [`<path d="${s.straps}" fill="none" stroke="#000" stroke-opacity="0.28" stroke-width="30" stroke-linecap="round"/>`,
      `<path d="${s.straps}" fill="none" stroke="${fill}" stroke-width="24" stroke-linecap="round"/>`] : []),
    `<path d="${s.outline}" fill="${fill}" stroke="#000" stroke-opacity="0.28" stroke-width="3" stroke-linejoin="round"/>`,
    ...(s.shade ? [`<path d="${s.shade}" fill="#000" fill-opacity="0.22"/>`] : []),
    ...s.details.map((d) => `<path d="${d}" ${faint}/>`),
    "</svg>",
  ].join("\n") + "\n";
}

// ---- the garments block: how the buyer's garments reach us and come back (spec section 2, v1 copy) ----

export const GARMENTS_TITLE = "Your garments";

// The lines under GARMENTS_TITLE on the designer, the placed page, the receipt, the request email and the Slack card.
// With the order number once there is one.
// The drop-off is our Bellwood shop, on purpose distinct from pickup ("Pickup in Altoona, PA").
export function garmentsBlock(orderNumber?: number): string[] {
  const label = orderNumber ? `order #${orderNumber}` : "your order number";
  return [
    `Drop them off at our Bellwood shop (1105 E 5th St), or ship them to 1105 E 5th St, Bellwood, PA 16617 with ${label} on the box.`,
    GARMENTS_WINDOW,
    "We take clean, unworn garments in cotton, poly or blends. No nylon or waterproof shells.",
    "We test-press one garment before the run.",
    "We are not responsible for defects in supplied garments.",
    "If the count does not match your order, we send you a note before printing.",
    "Getting them back: pick them up, or send a prepaid return label with your garments.",
  ];
}
